import type { ComponentType, ReactNode, JSX } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { lazyWithReload } from '@lib/lazyWithReload';
import { PermissionGate } from '@components/composition/PermissionGate';
import { FlagGate } from '@components/composition/FlagGate';
import { PLUGIN_TEMPLATES_FLAG, PLUGIN_TEMPLATES_FLAG_DEFAULT } from './Templates/flag';
import { REQUEST_ACCESS_FLAG, REQUEST_ACCESS_FLAG_DEFAULT } from './AccessRequests/flag';
import { VENDOR_PLUGIN_MANAGEMENT_FLAG, VENDOR_PLUGIN_MANAGEMENT_FLAG_DEFAULT } from './WindDown/flag';
import { MERCHANT_MIGRATION_FLAG, MERCHANT_MIGRATION_FLAG_DEFAULT } from './VersionManagement/flag';

const PluginsPage = lazyWithReload(() =>
  import('./Plugins/PluginsPage').then((m) => ({ default: m.PluginsPage })),
);
const ConnectionDetailsPage = lazyWithReload(() =>
  import('./ConnectionDetails').then((m) => ({
    default: m.ConnectionDetailsPage,
  })),
);
const PluginAboutPage = lazyWithReload(() =>
  import('./PluginAbout').then((m) => ({ default: m.PluginAboutPage })),
);
const ManagePluginsPage = lazyWithReload(() =>
  import('./IntegrationsManager').then((m) => ({
    default: m.ManagePluginsPage,
  })),
);
const IntegrationDetailsPage = lazyWithReload(() =>
  import('./IntegrationDetails').then((m) => ({
    default: m.IntegrationDetailsPage,
  })),
);
const AiEditorPage = lazyWithReload(() =>
  import('./IntegrationDetails/AiEditor').then((m) => ({
    default: m.AiEditor,
  })),
);
const BasicSettingsPage = lazyWithReload(() =>
  import('./BasicSettings').then((m) => ({
    default: m.BasicSettingsPage,
  })),
);
const InstantiateWizardPage = lazyWithReload(() =>
  import('./Templates/InstantiateWizard').then((m) => ({
    default: m.InstantiateWizardPage,
  })),
);
const AccessRequestsPage = lazyWithReload(() =>
  import('./AccessRequests/AccessRequestsPage').then((m) => ({
    default: m.AccessRequestsPage,
  })),
);
const ConnectedMerchantsPage = lazyWithReload(() =>
  import('./ConnectedMerchants/ConnectedMerchantsPage').then((m) => ({
    default: m.ConnectedMerchantsPage,
  })),
);
const MigrationPage = lazyWithReload(() =>
  import('./VersionManagement/MigrationPage').then((m) => ({
    default: m.MigrationPage,
  })),
);
const ReviewMappingsPage = lazyWithReload(() =>
  import('./VersionManagement/ReviewMappingsPage').then((m) => ({
    default: m.ReviewMappingsPage,
  })),
);
const NotFoundPage = lazyWithReload(() =>
  import('@components/composition/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);

interface PluginsRoutesProps {
  /** The app's per-route Suspense + error boundary; each route keeps its own name. */
  Slot: ComponentType<{ name: string; children: ReactNode }>;
}

/**
 * Plugin routes, mounted under `/admin/plugins/*`. Every route carries its own
 * permission gate, so URL access is blocked even when the sidebar link is hidden.
 */
export function PluginsRoutes({ Slot }: Readonly<PluginsRoutesProps>): JSX.Element {
  const versionMigration = (page: JSX.Element): JSX.Element => (
    <PermissionGate resource="integration" action="plugins_create" fallback={<Navigate to="/home" replace />}>
      <Slot name="version-migration">
        <FlagGate flag={MERCHANT_MIGRATION_FLAG} defaultValue={MERCHANT_MIGRATION_FLAG_DEFAULT} fallback={<NotFoundPage />}>
          {page}
        </FlagGate>
      </Slot>
    </PermissionGate>
  );

  return (
    <Routes>
      <Route
        index
        element={
          <PermissionGate
            anyOf={[
              { resource: "integration", action: "plugins_get" },
              { resource: "integration", action: "plugins_create" }
            ]}
            fallback={<Navigate to="/home" replace />}
          >
            <Slot name="plugins"><PluginsPage /></Slot>
          </PermissionGate>
        }
      />
      <Route
        path="connection-details/:merchantPluginId"
        element={
          <PermissionGate
            resource="integration"
            action="plugins_get"
            fallback={<Navigate to="/home" replace />}
          >
            <Slot name="plugins-connection-details">
              <ConnectionDetailsPage />
            </Slot>
          </PermissionGate>
        }
      />
      {/*
        Connection-details tabs get dedicated routes for parity with legacy
        (main: Integrations/index.js declared explicit per-tab Routes), so a
        Vendor plugin's Shipping/Product Mapping tabs are deep-linkable and a
        refresh stays on the open tab instead of snapping back to Variables.
        The static `connection-details` + tab segments out-rank the dynamic
        `:pluginId/:pluginVersionId/:tab` route below, so no shadowing.
      */}
      <Route
        path="connection-details/:merchantPluginId/shipping-mapping"
        element={
          <PermissionGate
            resource="integration"
            action="plugins_get"
            fallback={<Navigate to="/home" replace />}
          >
            <Slot name="plugins-connection-details">
              <ConnectionDetailsPage />
            </Slot>
          </PermissionGate>
        }
      />
      <Route
        path="connection-details/:merchantPluginId/warehouse-map"
        element={
          <PermissionGate
            resource="integration"
            action="plugins_get"
            fallback={<Navigate to="/home" replace />}
          >
            <Slot name="plugins-connection-details">
              <ConnectionDetailsPage />
            </Slot>
          </PermissionGate>
        }
      />
      <Route
        path="connection-details/:merchantPluginId/product-mapping"
        element={
          <PermissionGate
            resource="integration"
            action="plugins_get"
            fallback={<Navigate to="/home" replace />}
          >
            <Slot name="plugins-connection-details">
              <ConnectionDetailsPage />
            </Slot>
          </PermissionGate>
        }
      />
      <Route
        path="connection-details/:merchantPluginId/tax-code-mapping"
        element={
          <PermissionGate
            resource="integration"
            action="plugins_get"
            fallback={<Navigate to="/home" replace />}
          >
            <Slot name="plugins-connection-details">
              <ConnectionDetailsPage />
            </Slot>
          </PermissionGate>
        }
      />
      <Route
        path=":pluginId/about"
        element={
          <PermissionGate
            anyOf={[
              { resource: 'integration', action: 'plugins_get' },
              { resource: 'integration', action: 'plugins_create' },
            ]}
            fallback={<Navigate to="/home" replace />}
          >
            <Slot name="plugin-about">
              {/* Learn More ships dark outside dev; flag-off environments
                  404 this route just like the legacy portal did. */}
              <FlagGate flag="omTech21475VendorAboutPage" fallback={<NotFoundPage />}>
                <PluginAboutPage />
              </FlagGate>
            </Slot>
          </PermissionGate>
        }
      />
      <Route
        path="manage-plugins"
        element={
          <PermissionGate
            anyOf={[
              { resource: "integration", action: "plugins_get" },
              { resource: "integration", action: "plugins_create" }
            ]}
            fallback={<Navigate to="/home" replace />}
          >
            <Slot name="manage-plugins">
              <ManagePluginsPage />
            </Slot>
          </PermissionGate>
        }
      />
      <Route
        path="create-plugin"
        element={
          <PermissionGate
            resource="integration"
            action="plugins_create"
            fallback={<Navigate to="/home" replace />}
          >
            <Slot name="basic-settings">
              <BasicSettingsPage />
            </Slot>
          </PermissionGate>
        }
      />
      {/*
        Template instantiation wizard. There is no sidebar entry — Templates
        is a tab inside the Plugins page and this is where its CTA lands.
        Declared ahead of `:pluginId/:pluginVersionId/:tab` for readability;
        React Router ranks the static `templates` segment above the dynamic
        one regardless of order, so the two can't shadow each other.

        Flag-off deep links 404 rather than redirect, matching how the
        plugin About page handles its own gate. The permission gate is
        `plugins_create` ("Manage Plugins"), matching the CTA in
        TemplatesTab. integration-service enforces `plugins_instantiate`,
        but that scope is `PortalIsVisible: false` and user-service strips
        hidden permissions from the `/v1/login` response, so gating on it here
        would redirect every user to /home. It is a dependent of
        `plugins_create` for vendors, so the parent implies it.
      */}
      <Route
        path="templates/:templateId/create"
        element={
          <PermissionGate
            resource="integration"
            action="plugins_create"
            fallback={<Navigate to="/home" replace />}
          >
            <Slot name="instantiate-template">
              <FlagGate
                flag={PLUGIN_TEMPLATES_FLAG}
                defaultValue={PLUGIN_TEMPLATES_FLAG_DEFAULT}
                fallback={<NotFoundPage />}
              >
                <InstantiateWizardPage />
              </FlagGate>
            </Slot>
          </PermissionGate>
        }
      />
      {/*
        The plugin owner's access-request queue. Declared ahead
        of `:pluginId/…` for readability; React Router ranks the static
        `access-requests` segment above the dynamic one either way.

        The permission gate is `plugins_create` ("Manage Plugins") for the
        same reason the templates route above uses it: the scope
        integration-service enforces is `plugin_access_requests_manage`,
        which is `PortalIsVisible: false`, and user-service strips hidden
        permissions from the `/v1/login` response — gating on it directly would
        redirect every user to /home. It is a dependent of vendor
        `plugins_create`, so the visible parent implies it.

        Flag-off deep links 404 rather than redirect, matching the templates
        wizard and the plugin About page.
      */}
      <Route
        path="access-requests"
        element={
          <PermissionGate
            resource="integration"
            action="plugins_create"
            fallback={<Navigate to="/home" replace />}
          >
            <Slot name="access-requests">
              <FlagGate
                flag={REQUEST_ACCESS_FLAG}
                defaultValue={REQUEST_ACCESS_FLAG_DEFAULT}
                fallback={<NotFoundPage />}
              >
                <AccessRequestsPage />
              </FlagGate>
            </Slot>
          </PermissionGate>
        }
      />
      {/*
        Manage Connections. Flag-off deep links 404, like the templates wizard.
        integration-service enforces plugin ownership and answers a non-owner with 403.
      */}
      <Route
        path=":pluginId/connections"
        element={
          <PermissionGate
            resource="integration"
            action="plugins_create"
            fallback={<Navigate to="/home" replace />}
          >
            <Slot name="connected-merchants">
              <FlagGate
                flag={VENDOR_PLUGIN_MANAGEMENT_FLAG}
                defaultValue={VENDOR_PLUGIN_MANAGEMENT_FLAG_DEFAULT}
                fallback={<NotFoundPage />}
              >
                <ConnectedMerchantsPage />
              </FlagGate>
            </Slot>
          </PermissionGate>
        }
      />
      <Route
        path=":pluginId/basic-settings"
        element={
          <PermissionGate
            resource="integration"
            action="plugins_create"
            fallback={<Navigate to="/home" replace />}
          >
            <Slot name="basic-settings">
              <BasicSettingsPage />
            </Slot>
          </PermissionGate>
        }
      />
      <Route
        path=":pluginId/:pluginVersionId"
        element={
          <PermissionGate
            resource="integration"
            action="plugins_create"
            fallback={<Navigate to="/home" replace />}
          >
            <Slot name="integration-details">
              <IntegrationDetailsPage />
            </Slot>
          </PermissionGate>
        }
      />
      <Route
        path=":pluginId/:pluginVersionId/ai-editor"
        element={
          <PermissionGate
            resource="integration"
            action="plugins_create"
            fallback={<Navigate to="/home" replace />}
          >
            <Slot name="ai-editor">
              <AiEditorPage />
            </Slot>
          </PermissionGate>
        }
      />
      {/* Reached from the Version Management tab, behind the same Manage Plugins
          gate. The endpoints' Manage Connections scope is a hidden dependent of it
          and is not in every role's permission set. Flag-off deep links 404. */}
      <Route path=":pluginId/:pluginVersionId/version-management/migrate" element={versionMigration(<MigrationPage />)} />
      <Route path=":pluginId/:pluginVersionId/version-management/review" element={versionMigration(<ReviewMappingsPage />)} />
      <Route
        path=":pluginId/:pluginVersionId/:tab"
        element={
          <PermissionGate
            resource="integration"
            action="plugins_create"
            fallback={<Navigate to="/home" replace />}
          >
            <Slot name="integration-details">
              <IntegrationDetailsPage />
            </Slot>
          </PermissionGate>
        }
      />
      {/* Unmatched plugin paths are the app's 404, as before this table moved here. */}
      <Route path="*" element={<Slot name="not-found"><NotFoundPage /></Slot>} />
    </Routes>
  );
}
